import '@fontsource/outfit/300.css'
import '@fontsource/outfit/400.css'
import '@fontsource/outfit/500.css'
import '@fontsource/outfit/600.css'
import '@fontsource/outfit/700.css'
import '@/index.css'
import { OpenInNewTab } from '@/components/ui/open-in-new-tab'
import { PageLoader } from '@/components/ui/page-loader'
import { isSafeToReload, startAppUpdates } from '@/lib/app-update'
import { ErrorAlert } from '@/components/ui/error-alert'
import { initAuth, SignInReturnError } from '@/lib/msal-config'
import { router } from '@/lib/router'
import { initTelemetry, trackException } from '@/lib/telemetry'
import { isInIframe } from '@/lib/utils'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'

initTelemetry()

window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.code === 'ERR_CANCELED') return
  trackException(event.reason, { context: 'unhandledrejection' })
})

// Registers the self-destroying SW (vite.config) so previously cached clients
// unregister and clear caches on their next visit.
registerSW()

// With no SW precache, a tab open across a deploy can fail to fetch a purged
// hashed chunk. Reload once to pick up the new bundle; throttle so a persistent
// failure surfaces to the router boundary instead of looping.
const CHUNK_RELOAD_AT_KEY = 'cpq-chunk-reload-at'
let chunkReloadScheduled = false
window.addEventListener('vite:preloadError', (event) => {
  // Vite re-fires the event for the same failed import; keep suppressing
  // while the reload is pending.
  if (chunkReloadScheduled) {
    event.preventDefault()
    return
  }
  // In an iframe the app only renders the open-in-new-tab prompt; reloading
  // there re-triggers its auto-open and duplicates tabs (see b04d96a).
  if (isInIframe) return
  // Offline reload would land on the browser error page; let the boundary show.
  if (!navigator.onLine) return
  let lastReload = 0
  try {
    lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY)) || 0
  } catch {
    return // storage blocked — can't record an attempt, don't risk a reload loop
  }
  const sinceLastReload = Date.now() - lastReload
  if (sinceLastReload >= 0 && sinceLastReload < 15_000) return
  try {
    sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(Date.now()))
  } catch {
    return
  }
  chunkReloadScheduled = true
  event.preventDefault()
  window.location.reload()
})

const rootElement = document.getElementById('root')!

async function bootstrap() {
  const root = ReactDOM.createRoot(rootElement)

  // Embedded in Dynamics the app only offers to open itself in a tab: no
  // sign-in, data or router behind the prompt.
  if (isInIframe) {
    root.render(<OpenInNewTab />)
    return
  }

  // Moves this tab onto each new deploy while the user is away (dev builds
  // have no version.json).
  if (import.meta.env.PROD) startAppUpdates({ buildId: __BUILD_ID__, isSafeToReload })

  // The MSAL v3 build kept a plain access-token copy in this tab's storage.
  try {
    sessionStorage.removeItem('token-cache-storage')
  } catch {
    // storage blocked: nothing was kept there either
  }

  root.render(<PageLoader />)
  // A sign-in return is finished (and its quote URL restored) before any
  // route renders, so no screen ever sees a half-signed-in state.
  try {
    if (await initAuth()) return
  } catch (error) {
    if (!(error instanceof SignInReturnError)) throw error
    // A failed initialize leaves the response (code, client info) in the URL;
    // keep it out of the address bar and the support email.
    history.replaceState(null, '', window.location.pathname + window.location.search)
    root.render(
      <ErrorAlert
        title="Error signing in"
        message={error.message}
        onRetry={() => window.location.replace(error.target)}
      />
    )
    return
  }

  root.render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>
  )
}

if (!rootElement.innerHTML) void bootstrap()
